import React, { useState, useEffect, useCallback } from 'react';
import { Entity, BillboardGraphics } from "resium";
import { Cartesian3, Color, VerticalOrigin, Cartesian2, NearFarScalar } from "cesium";

/**
 * Flood_IoT_sensor Component
 * 
 * Renders an IoT sensor entity on the map that simulates a flood monitoring sensor.
 * The sensor updates its value every few seconds, simulating real-time data.
 * 
 * Props:
 *  - onSelect: Callback function triggered when the sensor is clicked, receives sensor data.
 *  - onUpdate: Callback function triggered when sensor data changes, receives updated sensor data.
 */
const Flood_IoT_sensor = ({ onSelect, onUpdate }) => {
  // Define the specific coordinates as requested
  const latitude = 3.096810;
  const longitude = 101.553963;
  
  // Create state for the dynamic sensor value
  const [sensorValue, setSensorValue] = useState(13.2);

  // Create sensor data object - moved outside useEffect to be reused
  const createSensorData = useCallback((value) => {
    return {
      id: "floods-iot-001",
      name: "Flood Monitoring Sensor #1",
      type: "Water Level Sensor",
      value: value,
      unit: "m",
      status: value > 13.5 ? "Critical" : value > 13.0 ? "Warning" : "Normal",
      batteryLevel: "85%",
      signalStrength: "Good",
      alertThreshold: "13.5m",
      lastUpdated: new Date().toISOString(),
      location: {
        latitude: latitude,
        longitude: longitude,
        description: "Sungai Perlis, Kangar"
      },
      modelNumber: "WL-3000X",
      manufacturer: "AquaTrack Technologies",
      dataFrequency: "Every 5 minutes",
      installationDate: "2023-06-15",
      maintenanceStatus: "Operational",
      lastMaintenance: "2023-12-10"
    };
  }, [latitude, longitude]);

  // Simulate sensor data updates
  useEffect(() => {
    const interval = setInterval(() => {
      // Generate a random value between 12.0 and 14.0 to simulate water level changes
      const newValue = 12.0 + Math.random() * 2.0;
      const formattedValue = parseFloat(newValue.toFixed(1));
      
      // Update local state
      setSensorValue(formattedValue);
      
      // Notify parent component if callback exists
      if (onUpdate) {
        onUpdate(createSensorData(formattedValue));
      }
    }, 3000); // Update every 3 seconds

    return () => clearInterval(interval); // Clean up on unmount
  }, [onUpdate, createSensorData]);

  // Handle click event
  const handleClick = () => {
    if (onSelect) {
      onSelect(createSensorData(sensorValue));
    }
  };

  // Determine status for styling
  const getStatusColor = (value) => {
    if (value > 13.5) return "#E53935"; // Red for critical
    if (value > 13.0) return "#FF9800"; // Orange for warning
    return "#43A047"; // Green for normal
  };
  
  // Dynamic color based on current value
  const statusColor = getStatusColor(sensorValue);
  
  // Create a modern, sleek sensor icon using SVG
  const createModernSensorIcon = (value, color) => {
    // Format the value text (13.2m)
    const valueText = `${value}m`;
    
    // Create an SVG for a modern, sleek IoT sensor visualization
    const svg = `
      <svg width="140" height="140" viewBox="0 0 140 140" xmlns="http://www.w3.org/2000/svg">
        <!-- Main circle with glass effect -->
        <circle cx="70" cy="70" r="65" fill="rgba(255,255,255,0.95)" stroke="${color}" stroke-width="3" />
        
        <!-- Inner circles for sensor visualization -->
        <circle cx="70" cy="70" r="55" fill="rgba(240,240,240,0.8)" />
        <circle cx="70" cy="70" r="45" fill="${color}" fill-opacity="0.15" />
        
        <!-- Pulsating circle -->
        <circle cx="70" cy="70" r="35" fill="${color}" fill-opacity="0.3">
          <animate attributeName="r" values="30;40;30" dur="2s" repeatCount="indefinite" />
          <animate attributeName="fill-opacity" values="0.3;0.1;0.3" dur="2s" repeatCount="indefinite" />
        </circle>
        
        <!-- Inner dot -->
        <circle cx="70" cy="70" r="8" fill="${color}" />
        
        <!-- Value display -->
        <text x="70" y="60" font-family="Arial, sans-serif" font-size="24" font-weight="bold" text-anchor="middle" fill="#333333">${valueText}</text>
        
        <!-- Subtle text label -->
        <text x="70" y="85" font-family="Arial, sans-serif" font-size="12" font-weight="500" text-anchor="middle" fill="#555555">Water Level</text>
        
        <!-- Top small indicator dot -->
        <circle cx="70" cy="25" r="4" fill="${color}">
          <animate attributeName="fill-opacity" values="1;0.6;1" dur="1.5s" repeatCount="indefinite" />
        </circle>
      </svg>
    `;
    
    // Convert SVG to data URL
    return `data:image/svg+xml;base64,${btoa(svg)}`;
  };

  // Get the icon image
  const sensorIcon = createModernSensorIcon(sensorValue, statusColor);

  return (
    <Entity
      position={Cartesian3.fromDegrees(longitude, latitude, 0)}
      name="Flood Sensor"
      description="IoT Flood Monitoring Sensor"
      onClick={handleClick}
    >
      <BillboardGraphics
        image={sensorIcon}
        verticalOrigin={VerticalOrigin.CENTER}
        scale={0.75} // Adjusted to 0.75 - between the original 0.5 and doubled 1.0
        scaleByDistance={new NearFarScalar(1000, 1.5, 20000, 0.75)} // Adjusted scale values for balanced visibility
        disableDepthTestDistance={Number.POSITIVE_INFINITY} // Ensure the sensor is always visible
                      heightReference={0} // NONE - No terrain clamping for better performance
        translucencyByDistance={new NearFarScalar(1000, 1.0, 20000, 0.8)} // Slight transparency when far away
      />
    </Entity>
  );
};

export default Flood_IoT_sensor;